import { useState, useCallback, useEffect, useRef } from 'react';
import { queryAgent, getCurrentLocation, getConversationMessages } from '../services/api';
import { Message, LocationData } from '../types/chat';
import { getUserId } from '../services/userService';

const INIT_MESSAGE = 'Hello! My name is **Nia** and I\'m your personalized local agent powered by Nearvana. I can help you discover the best restaurants, must-see attractions, or unique activities around you—or help plan your next trip anywhere in the world.\n\nWhat are you in the mood for today? Are you looking for something fun to do nearby, a great place to eat, or do you have travel plans in mind?'

export const useChat = (initialSessionId?: string) => {
  // Get persistent user ID
  const userId = getUserId();

  // Get or create persistent session ID - updates when initialSessionId changes
  const [sessionId, setSessionId] = useState(() => {
    // If initialSessionId is provided from route, use it
    if (initialSessionId) {
      sessionStorage.setItem('nia_session_id', initialSessionId);
      return initialSessionId;
    }

    // Otherwise check sessionStorage
    const storedSessionId = sessionStorage.getItem('nia_session_id');
    if (storedSessionId) {
      return storedSessionId;
    }

    // Create new session ID
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('nia_session_id', newSessionId);
    return newSessionId;
  });

  // Update sessionId when initialSessionId changes (when navigating between conversations)
  useEffect(() => {
    if (initialSessionId && initialSessionId !== sessionId) {
      sessionStorage.setItem('nia_session_id', initialSessionId);
      setSessionId(initialSessionId);
    }
  }, [initialSessionId, sessionId]);

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get or restore user location from sessionStorage
  const [userLocation, setUserLocation] = useState<LocationData | null>(() => {
    const storedLocation = sessionStorage.getItem('nia_user_location');
    if (storedLocation) {
      try {
        return JSON.parse(storedLocation);
      } catch {
        return null;
      }
    }
    return null;
  });

  const addMessage = useCallback((content: string, type: 'user' | 'assistant' = 'user') => {
    const newMessage: Message = {
      id: Date.now().toString(),
      type,
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  }, []);

  const handleSendMessage = useCallback(async (userMessage?: string) => {
    const messageToSend = userMessage || inputValue;
    if (typeof messageToSend !== 'string') return
    if (!messageToSend || !messageToSend.trim() || isLoading) return;

    addMessage(messageToSend, 'user');
    setInputValue('');
    setIsLoading(true);

    try {
      // Always send user location and userId if available - backend will use them when relevant
      const response = await queryAgent(messageToSend, userLocation, sessionId, userId);

      if (response.success && response.messages) {
        let lastMessage: { role: string; content: string; tool_calls?: any; tool_call_id?: string } | null = null;
        for (let i = response.messages.length - 1; i >= 0; i--) {
          const msg = response.messages[i];
          if (msg.content && msg.content.trim() && (msg.role === 'AI' || msg.role.startsWith('TOOL'))) {
            lastMessage = msg;
            break;
          }
        }

        if (lastMessage && lastMessage.content) {
          addMessage(lastMessage.content, 'assistant');
        } else {
          addMessage('I received your message but couldn\'t process it properly. Please try again.', 'assistant');
        }
      } else {
        addMessage('Sorry, I encountered an error processing your request. Please try again.', 'assistant');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      addMessage('Sorry, I\'m having trouble connecting to the server. Please check your connection and try again.', 'assistant');
    } finally {
      setIsLoading(false);
    }
  }, [userLocation, sessionId, isLoading, addMessage, inputValue]);

  const handleLocationShare = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    addMessage('Sharing my location...', 'user');

    try {
      console.log('Starting location request...');
      const location = await getCurrentLocation();
      console.log('Location received:', location);

      setUserLocation(location);
      // Persist location to sessionStorage
      sessionStorage.setItem('nia_user_location', JSON.stringify(location));

      addMessage(`Perfect! I have your location (${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}). Now you can ask me to find events, restaurants, or places near you. What would you like to discover?`, 'assistant');
    } catch (error) {
      console.error('Error sharing location:', error);
      if (error instanceof Error) {
        // Use the more specific error message from getCurrentLocation
        addMessage(error.message || 'I couldn\'t access your location. Please tell me where you are or what you\'re looking for!', 'assistant');
        } else {
          addMessage('I couldn\'t access your location. Please tell me where you are or what you\'re looking for!', 'assistant');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, addMessage]);

  const handleQuickAction = useCallback((action: string) => {
    setInputValue(action);
    handleSendMessage(action);
  }, [handleSendMessage]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
      setInputValue('');
    }
  }, [handleSendMessage, inputValue]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const loadConversation = async () => {
      // Check if this is a conversation being loaded (has a route session ID)
      const isLoadingExistingConversation = initialSessionId !== undefined;

      if (isLoadingExistingConversation && sessionId) {
        setIsLoading(true);
        try {
          console.log('Loading conversation for session:', sessionId);
          const response = await getConversationMessages(sessionId);

          if (response.success && response.messages && response.messages.length > 0) {
            // Convert API messages to UI messages
            const loadedMessages: Message[] = [];

            for (const msg of response.messages) {
              // Only show USER and AI messages (skip TOOL messages)
              if (msg.role === 'USER') {
                loadedMessages.push({
                  id: `${Date.now()}_${Math.random()}`,
                  type: 'user',
                  content: msg.content,
                  timestamp: new Date(),
                });
              } else if (msg.role === 'AI' && msg.content && msg.content.trim()) {
                loadedMessages.push({
                  id: `${Date.now()}_${Math.random()}`,
                  type: 'assistant',
                  content: msg.content,
                  timestamp: new Date(),
                });
              }
            }

            console.log('Loaded messages:', loadedMessages.length);
            setMessages(loadedMessages);
          } else {
            console.log('No messages found for session:', sessionId);
            // No messages found, show welcome message
            setMessages([
              {
                id: 'welcome',
                type: 'assistant',
                content: INIT_MESSAGE,
                timestamp: new Date(),
              }
            ]);
          }
        } catch (error) {
          console.error('Error loading conversation:', error);
          // On error, show welcome message
          setMessages([
            {
              id: 'welcome',
              type: 'assistant',
              content: INIT_MESSAGE,
              timestamp: new Date(),
            }
          ]);
        } finally {
          setIsLoading(false);
        }
      } else if (!isLoadingExistingConversation) {
        // New conversation (no initialSessionId), show welcome message
        console.log('New conversation, showing welcome message');
        setMessages([
          {
            id: 'welcome',
            type: 'assistant',
            content: INIT_MESSAGE,
            timestamp: new Date(),
          }
        ]);
      }
    };

    loadConversation();
  }, [sessionId, initialSessionId]);

  return {
    messages,
    inputValue,
    setInputValue,
    isLoading,
    sessionId,
    userLocation,
    addMessage,
    handleSendMessage,
    handleLocationShare,
    handleQuickAction,
    handleKeyPress,
    messagesEndRef,
  };
};

